import React from 'react';
import { Sun, Moon, Monitor, Palette } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';

const ThemeSettings = () => {
  const { theme, setLightTheme, setDarkTheme, toggleTheme } = useTheme();
  const { t } = useTranslation();

  const themeOptions = [
    {
      id: 'light',
      name: t('theme.light', 'Light'),
      description: t('theme.lightDescription', 'Clean and bright interface'),
      icon: Sun,
      action: setLightTheme
    },
    {
      id: 'dark', 
      name: t('theme.dark', 'Dark'),
      description: t('theme.darkDescription', 'Easy on the eyes in low light'),
      icon: Moon,
      action: setDarkTheme
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
          <Palette className="h-5 w-5 text-purple-600 dark:text-purple-400" />
        </div>
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            {t('theme.title', 'Theme Settings')}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('theme.description', 'Customize the appearance of your CRM interface')}
          </p>
        </div>
      </div>

      {/* Current Theme Display */}
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {t('theme.currentTheme', 'Current Theme')}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 capitalize">
              {theme} {t('theme.mode', 'mode')}
            </p>
          </div>
          <button
            onClick={toggleTheme}
            className="btn-secondary flex items-center space-x-2"
          >
            {theme === 'light' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
            <span>{t('theme.toggle', 'Toggle Theme')}</span>
          </button>
        </div>
      </div>

      {/* Theme Options */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
          {t('theme.selectTheme', 'Select Theme')}
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {themeOptions.map((option) => {
            const Icon = option.icon;
            const isActive = theme === option.id;
            
            return (
              <button
                key={option.id}
                onClick={option.action}
                className={`
                  relative p-4 rounded-lg border-2 text-left transition-all duration-200
                  ${isActive 
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 ring-2 ring-blue-500 ring-opacity-50' 
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600'
                  }
                `}
              >
                <div className="flex items-start space-x-3">
                  <div className={`
                    p-2 rounded-lg
                    ${isActive 
                      ? 'bg-blue-100 dark:bg-blue-800' 
                      : 'bg-gray-100 dark:bg-gray-700'
                    }
                  `}>
                    <Icon className={`
                      h-5 w-5
                      ${isActive 
                        ? 'text-blue-600 dark:text-blue-400' 
                        : 'text-gray-600 dark:text-gray-400'
                      }
                    `} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h5 className={`
                        font-medium
                        ${isActive 
                          ? 'text-blue-900 dark:text-blue-100' 
                          : 'text-gray-900 dark:text-gray-100'
                        }
                      `}>
                        {option.name}
                      </h5>
                      {isActive && (
                        <div className="h-2 w-2 bg-blue-500 rounded-full"></div>
                      )}
                    </div>
                    <p className={`
                      text-sm mt-1
                      ${isActive 
                        ? 'text-blue-700 dark:text-blue-300' 
                        : 'text-gray-500 dark:text-gray-400'
                      }
                    `}>
                      {option.description}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Additional Theme Info */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Monitor className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
              {t('theme.systemDetection', 'System Detection')}
            </p>
            <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
              {t('theme.systemDetectionDescription', 
                'The theme preference is automatically saved and will be remembered across sessions. If no preference is set, the system will follow your device\'s theme setting.'
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThemeSettings;